import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2, Pencil, Check, CheckCircle2, Trash2, X, Plus, ImagePlus, Sparkles, Copy } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { mergeLastCrearAnuncioState } from '../../utils/crearAnuncioStorage';
import { rubroVisible } from '../../lib/onboardingPendiente';
import { CustomSelect } from '../../components/CustomSelect';
import { TEXT_FONT_OPTIONS } from '../../lib/fontOptions';
import { getUserPlan, planAllowsMultiBrand } from '../../lib/planUtils';
import SubscriptionLimitModal from '../../components/SubscriptionLimitModal';
import { sumarLogoAElementos } from '../v2/elementosIniciales';

// Saca el header de título inicial que el LLM agrega (ej: "# Análisis Estratégico de Marca: X")
// — redundante porque el usuario ya está parado en la pill de esa marca.
// IMPORTANTE: no descartamos secciones numeradas ("1. Identidad de marca", "2. Público objetivo")
// — esas sí son contenido. Solo descartamos un título overall si aparece antes de las secciones.
const stripLeadingTitle = (text: string): string => {
  const lines = text.split('\n');
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') i++;
  if (i < lines.length && /^#{1,6}\s+/.test(lines[i])) {
    const content = lines[i].replace(/^#{1,6}\s+/, '').trim();
    // Solo descartar si NO empieza con un número de sección (1., 2., 1), etc.)
    if (!/^\d+\s*[\.\)]/.test(content)) {
      i++;
      while (i < lines.length && lines[i].trim() === '') i++;
      return lines.slice(i).join('\n');
    }
  }
  return text;
};

// Simple markdown renderer for brand analysis
const renderMarkdown = (text: string): React.ReactNode => {
  const lines = stripLeadingTitle(text).split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const flushList = () => {
    if (listItems.length > 0 && listType) {
      const ListTag = listType;
      elements.push(
        <ListTag key={elements.length} className={listType === 'ul' ? 'list-disc list-inside my-2' : 'list-decimal list-inside my-2'}>
          {listItems.map((item, i) => <li key={i}>{parseInline(item)}</li>)}
        </ListTag>
      );
      listItems = [];
      listType = null;
    }
  };

  const parseInline = (line: string): React.ReactNode => {
    // Bold: **text** or __text__
    // Italic: *text* or _text_
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let key = 0;

    while (remaining.length > 0) {
      // Bold — usamos font-semibold (600) para que matchee los headers ## (que también son 600)
      const boldMatch = remaining.match(/\*\*(.+?)\*\*|__(.+?)__/);
      if (boldMatch && boldMatch.index !== undefined) {
        if (boldMatch.index > 0) {
          parts.push(remaining.slice(0, boldMatch.index));
        }
        parts.push(<strong key={key++} className="font-semibold">{boldMatch[1] || boldMatch[2]}</strong>);
        remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
        continue;
      }

      // Italic
      const italicMatch = remaining.match(/\*(.+?)\*|_(.+?)_/);
      if (italicMatch && italicMatch.index !== undefined) {
        if (italicMatch.index > 0) {
          parts.push(remaining.slice(0, italicMatch.index));
        }
        parts.push(<em key={key++}>{italicMatch[1] || italicMatch[2]}</em>);
        remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
        continue;
      }

      parts.push(remaining);
      break;
    }

    return parts.length === 1 ? parts[0] : parts;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Headers (1-6 #) — todos se renderizan con el mismo estilo limpio que en el diseño viejo
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      flushList();
      const level = headerMatch[1].length;
      const text = headerMatch[2];
      if (level <= 2) {
        elements.push(<h3 key={elements.length} className="font-semibold text-brand-black/90 mt-4 mb-1">{parseInline(text)}</h3>);
      } else {
        elements.push(<h4 key={elements.length} className="font-semibold text-brand-black/90 mt-3 mb-1">{parseInline(text)}</h4>);
      }
      continue;
    }

    // Unordered list
    if (line.match(/^[-*]\s+/)) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      listItems.push(line.replace(/^[-*]\s+/, ''));
      continue;
    }

    // Ordered list
    if (line.match(/^\d+\.\s+/)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      listItems.push(line.replace(/^\d+\.\s+/, ''));
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      flushList();
      elements.push(<br key={elements.length} />);
      continue;
    }

    // Regular paragraph
    flushList();
    elements.push(<p key={elements.length} className="my-1">{parseInline(line)}</p>);
  }

  flushList();
  return elements;
};

interface CompanyDataProps {
  profile: any;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
  /**
   * Modo embebido (ej. tab "Mi diseño" de Mi marca): sin el container/card
   * exterior (los cards internos quedan sueltos, no anidados en uno más
   * grande) y sin padding horizontal propio. Además oculta el bloque del logo
   * (ya se muestra arriba en la card de marca).
   */
  embedded?: boolean;
  /**
   * Se llama una vez, cuando este bloque terminó de cargar sus datos (con éxito o
   * no) — ADR-2026-08-03-todas-las-secciones-cargan-igual.
   *
   * Existe porque `Mi marca` no puede saber por su cuenta cuándo está lista: las
   * tarjetas que más tardan (nombre, rubro, memoria de la IA) las carga este
   * componente. Es opcional: el otro consumidor (`/configuracion`) no lo pasa y no
   * cambia nada.
   */
  onListo?: () => void;
  /**
   * Abrir una edición al entrar — ADR-2026-10-02-mi-marca-agregar-directo. Lo manda el
   * "Agregar" de "Completá tu marca": en vez de dejar al usuario en el tab buscando el
   * lápiz, se abre directamente lo que falta (el selector de archivo del logo, o la
   * tarjeta de colores, tipografía o memoria en modo edición). `n` distingue dos
   * pedidos iguales seguidos; `onPedidoAtendido` avisa para que no se repita al volver
   * a montar.
   */
  pedido?: { que: 'logo' | 'colores' | 'tipografia' | 'memoria'; n: number } | null;
  onPedidoAtendido?: () => void;
}

interface CompanyProfileData {
  nombre: string | null;
  logo_url: string | null;
  rubro: string | null;
}

// Datos hardcodeados de marca (demo). Reemplazar por lectura de brand_profiles a futuro.
const BRAND_COLORS_DEMO: Array<{ hex: string; name: string; role: string }> = [
  { hex: '#000000', name: 'Negro Absoluto', role: 'primary' },
  { hex: '#B00020', name: 'Rojo Carmín', role: 'accent' },
  { hex: '#5D524D', name: 'Marrón Arcilla', role: 'secondary' },
  { hex: '#F8F5F0', name: 'Blanco Lino', role: 'neutral' },
  { hex: '#333333', name: 'Gris Grafito', role: 'neutral' },
];

const BRAND_FONTS_DEMO = {
  style: 'Serif elegante y refinada, peso regular, atemporalidad, artesanía y sofisticación discreta.',
  suggested: ['Cormorant Garamond', 'Lora'] as const,
};

/**
 * Si un color es claro, para escribir encima en negro y no en blanco (el hex de cada
 * muestra de la paleta). Luminancia relativa aproximada; un hex que no se entiende
 * cuenta como oscuro.
 */
const esColorClaro = (hex: string): boolean => {
  let h = String(hex || '').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6;
};

const ROLE_LABELS: Record<string, string> = {
  primary: 'Principal',
  secondary: 'Secundario',
  accent: 'Acento',
  neutral: 'Neutro',
};

/**
 * Botón de editar que se transforma según el estado:
 *  - no editando        → lápiz (entra a editar)
 *  - editando, con cambios → check azul (guardar)
 *  - editando, sin cambios → equis (cancelar)
 * Reemplaza a los botones Guardar/Cancelar de cada card.
 */
const EditConfirmButton: React.FC<{
  isEditing: boolean;
  hasChanges: boolean;
  saving?: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  label?: string;
}> = ({ isEditing, hasChanges, saving, onEdit, onSave, onCancel, label }) => {
  const base =
    'shrink-0 w-7 h-7 flex items-center justify-center rounded-md shadow-sm transition-colors disabled:opacity-60';
  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={onEdit}
        className={`${base} bg-surface-card border border-neutral-200 hover:bg-neutral-50`}
        aria-label={label ? `Editar ${label}` : 'Editar'}
      >
        <Pencil className="w-3 h-3 text-neutral-600" />
      </button>
    );
  }
  if (hasChanges) {
    return (
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className={`${base} bg-brand-primary hover:bg-brand-primary-hover border border-brand-primary text-white`}
        aria-label="Guardar"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onCancel}
      className={`${base} bg-surface-card border border-neutral-200 hover:bg-neutral-50`}
      aria-label="Cancelar"
    >
      <X className="w-3.5 h-3.5 text-neutral-600" />
    </button>
  );
};

export const CompanyData: React.FC<CompanyDataProps> = ({ profile, setError, setSuccess, embedded = false, onListo, pedido, onPedidoAtendido }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingName, setSavingName] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  /**
   * La URL del logo existe pero no carga (borrada del bucket, firma vencida): se trata
   * como si no hubiera logo. Guarda la URL que falló y no un booleano —el mismo patrón
   * que `AvatarMarca`—, así al cambiar de marca o al subir uno nuevo el estado se
   * levanta solo, sin un efecto de reset.
   */
  const [logoRoto, setLogoRoto] = useState<string | null>(null);
  const [showMultiBrandRequiredModal, setShowMultiBrandRequiredModal] = useState(false);
  const [company, setCompany] = useState<CompanyProfileData>({ nombre: '', logo_url: null, rubro: null });
  const [nameInput, setNameInput] = useState('');
  // Arrancan VACÍOS y no en "Moda e Indumentaria": ese default se ve como si el rubro
  // ya estuviera elegido y guardado, y una empresa que viene del alta por link todavía
  // no eligió ninguno. Vacío, `CustomSelect` muestra su placeholder — que es el estado
  // que esta pantalla ya sabe pintar. Falla cerrado: si la consulta se cae, el select
  // queda vacío en vez de afirmar un rubro que nadie dijo.
  const [rubroInput, setRubroInput] = useState('');
  const [rubroOption, setRubroOption] = useState<string>('');
  const [customRubro, setCustomRubro] = useState<string>('');
  const [savingRubro, setSavingRubro] = useState(false);
  const [marketingStrategy, setMarketingStrategy] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isStrategyExpanded, setIsStrategyExpanded] = useState(false);
  // Índice del color cuyo hex se acaba de copiar (muestra "Copiado" un instante) —
  // ADR-2026-10-02-mi-marca-informacion-visual.
  const [hexCopiado, setHexCopiado] = useState<number | null>(null);
  const copiarHex = (hex: string, idx: number) => {
    void navigator.clipboard?.writeText(hex.toUpperCase()).then(() => {
      setHexCopiado(idx);
      window.setTimeout(() => setHexCopiado((v) => (v === idx ? null : v)), 1200);
    });
  };
  const [isEditingMemoria, setIsEditingMemoria] = useState(false);
  const [memoriaInput, setMemoriaInput] = useState('');
  const [savingMemoria, setSavingMemoria] = useState(false);
  const [isEditingFonts, setIsEditingFonts] = useState(false);
  const [fontsInput, setFontsInput] = useState<string[]>([]);
  const [savingFonts, setSavingFonts] = useState(false);
  const [isEditingColors, setIsEditingColors] = useState(false);
  const [colorsInput, setColorsInput] = useState<
    Array<{ hex: string; name?: string; role?: string }>
  >([]);
  const [savingColors, setSavingColors] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAnalysis, setDeletingAnalysis] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Multi-marca: lista de brands del company + marca activa visualizada
  type BrandRow = {
    id: string;
    name: string;
    rubro: string | null;
    logo_url: string | null;
    is_default: boolean;
    context: string | null;
    colors: any;
    fonts: any;
    created_at: string;
  };
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [activeBrandId, setActiveBrandId] = useState<string | null>(null);
  const [showDeleteBrandModal, setShowDeleteBrandModal] = useState(false);
  const [deletingBrand, setDeletingBrand] = useState(false);

  // Pills bar: drag-to-scroll en desktop (en mobile el swipe nativo ya funciona)
  const pillsScrollRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef({
    active: false,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });

  const onPillsMouseDown = (e: React.MouseEvent) => {
    const el = pillsScrollRef.current;
    if (!el) return;
    dragStateRef.current = {
      active: true,
      startX: e.pageX,
      startScrollLeft: el.scrollLeft,
      moved: false,
    };
  };

  const onPillsMouseMove = (e: React.MouseEvent) => {
    const state = dragStateRef.current;
    if (!state.active) return;
    const el = pillsScrollRef.current;
    if (!el) return;
    const dx = e.pageX - state.startX;
    if (Math.abs(dx) > 5) state.moved = true;
    if (state.moved) {
      e.preventDefault();
      el.scrollLeft = state.startScrollLeft - dx;
    }
  };

  const stopPillsDrag = () => {
    dragStateRef.current.active = false;
    // Limpiamos "moved" en el próximo frame para que el onClick
    // del pill pueda leerlo antes de que se resetee.
    requestAnimationFrame(() => {
      dragStateRef.current.moved = false;
    });
  };

  const activeBrand = useMemo(
    () => brands.find((b) => b.id === activeBrandId) || null,
    [brands, activeBrandId]
  );

  // Cargar Google Fonts (carga on-demand cuando hay fonts a previsualizar — manejado más abajo).

  // Cargar las brands del company + setear la activa
  const fetchBrands = useCallback(async () => {
    if (!profile?.id_company) return;
    try {
      const { data, error } = await supabase
        .from('brand_profiles')
        .select('id, name, rubro, logo_url, is_default, context, colors, fonts, created_at')
        .eq('id_company', profile.id_company)
        .eq('is_draft', false)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true });
      if (error) throw error;
      // Esta pantalla tiene su propia consulta de marcas (no pasa por `useBrandProfile`),
      // así que el centinela se sanea también acá — si no, el select de rubro lo muestra
      // crudo en el input de "Otro".
      const list = ((data as BrandRow[] | null) ?? []).map((b) => ({
        ...b,
        rubro: rubroVisible(b.rubro),
      }));
      setBrands(list);

      // Pick active brand: current user's current_brand_id → is_default → first
      const { data: { user } } = await supabase.auth.getUser();
      let pick: string | null = null;
      if (user) {
        const { data: prof } = await supabase
          .from('profiles')
          .select('current_brand_id')
          .eq('id', user.id)
          .single();
        pick = (prof as any)?.current_brand_id ?? null;
      }
      if (!pick || !list.find((b) => b.id === pick)) {
        pick = list.find((b) => b.is_default)?.id ?? list[0]?.id ?? null;
      }
      setActiveBrandId(pick);
    } catch (e) {
      // Silencioso: si falla, queda en modo legacy (sin brands)
    }
  }, [profile?.id_company]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // En modo embebido ("Mi diseño") ya no hay pills internas para cambiar de
  // marca: el switcher vive en la card de marca de arriba (MiMarca), que dispara
  // `atlas:brand-changed`. Escuchamos ese evento para mantener la marca activa
  // visualizada en sync con la marca global.
  useEffect(() => {
    if (!embedded) return;
    const onBrandChanged = (e: Event) => {
      const brandId = (e as CustomEvent<{ brandId?: string }>).detail?.brandId;
      if (brandId) setActiveBrandId(brandId);
    };
    window.addEventListener('atlas:brand-changed', onBrandChanged);
    return () => window.removeEventListener('atlas:brand-changed', onBrandChanged);
  }, [embedded]);

  // Ref para trackear qué brand id corresponde al estado actual de los inputs.
  // Sin esto, al cambiar de pill el formulario queda con valores de la marca anterior
  // por un frame y el botón "Guardar" del rubro aparece como si hubiera cambios.
  const rubroStateBrandIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeBrand) return;
    setNameInput(activeBrand.name || '');
    // `?? ''` y no `|| 'Moda e Indumentaria'`: una marca sin rubro —la del alta por
    // link, hasta que el cuestionario escriba el de verdad— tiene que verse VACÍA.
    // Decirle "Moda e Indumentaria" es afirmarle un rubro que nunca eligió, y encima
    // el botón Guardar no aparecía (el valor mostrado coincidía con el "actual"), así
    // que no tenía forma de corregirlo.
    const rubroVal = activeBrand.rubro ?? '';
    setRubroInput(rubroVal);
    if (rubroVal === '') {
      setRubroOption('');
      setCustomRubro('');
    } else if (rubros.includes(rubroVal)) {
      setRubroOption(rubroVal);
      setCustomRubro('');
    } else {
      setRubroOption('Otro');
      setCustomRubro(rubroVal);
    }
    rubroStateBrandIdRef.current = activeBrand.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrand?.id]);

  // Cuando cambia la marca activa, cargar Google Fonts para previsualizar tipografía
  useEffect(() => {
    const suggested = (activeBrand?.fonts as any)?.suggested as string[] | undefined;
    if (!Array.isArray(suggested) || suggested.length === 0) return;
    const families = suggested
      .map((f) => f.trim().replace(/\s+/g, '+'))
      .filter(Boolean)
      .map((f) => `family=${f}:wght@400;500;600`)
      .join('&');
    if (!families) return;
    const id = `brand-fonts-${activeBrand?.id}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    document.head.appendChild(link);
  }, [activeBrand?.id, activeBrand?.fonts]);

  const rubros = [
    'Moda e Indumentaria',
    'Hogar y Decoración',
    'Belleza y Cuidado Personal',
    'Tecnología',
    'Salud y Bienestar',
    'Automotriz',
    'Gastronomía',
    'Juguetería',
    'Arte y Manualidades',
    'Librería',
    'Creación de Contenido',
    'Otro'
  ];

  useEffect(() => {
    const loadData = async () => {
      if (!profile?.id_company) {
        setLoading(false);
        return;
      }

      try {
        setError(null);
        // Fetch company profile (name and logo)
        const { data: companyData, error: companyError } = await supabase
          .from('company_profiles')
          .select('nombre, logo_url, rubro')
          .eq('id_company', profile.id_company)
          .single();

        if (companyError) throw companyError;

        setCompany({ nombre: companyData?.nombre ?? '', logo_url: companyData?.logo_url ?? null, rubro: rubroVisible(companyData?.rubro) });
        // Los inputs de nombre y rubro son de la MARCA activa cuando hay una: los
        // carga el efecto de `activeBrand`. Esta consulta y la de las marcas corren en
        // paralelo, y si ésta volvía última pisaba el rubro de la marca con el de la
        // empresa: "Gastronomía" + "Guardar" en una marca de Moda, y tocarlo le
        // cambiaba el rubro (ADR-2026-10-02-mi-marca-informacion-en-tercios). Sólo se
        // cargan desde la empresa si todavía no se sincronizó ninguna marca; si una
        // llega después, su efecto los pisa igual.
        if (rubroStateBrandIdRef.current === null) {
          setNameInput(companyData?.nombre ?? '');
          // Mismo criterio que el efecto de la marca activa: sin rubro, vacío.
          const rubroVal = rubroVisible(companyData?.rubro) ?? '';
          setRubroInput(rubroVal);
          if (rubroVal === '') {
            setRubroOption('');
            setCustomRubro('');
          } else if (rubros.includes(rubroVal)) {
            setRubroOption(rubroVal);
            setCustomRubro('');
          } else {
            setRubroOption('Otro');
            setCustomRubro(rubroVal);
          }
        }

        // Fetch marketing strategy from company schema config row 0
        const { data: cfg, error: cfgError } = await supabase
          .from('config')
          .select('media_agent_analysis')
          .eq('id_company', profile.id_company)
          .maybeSingle();

        if (!cfgError) {
          setMarketingStrategy(cfg?.media_agent_analysis ?? null);
        } else if (cfgError.code !== 'PGRST116') {
          // Ignore no rows error; otherwise raise
          throw cfgError;
        }
      } catch (err) {
        console.error('Error loading company data:', err);
        setError('Error al cargar los datos de la empresa');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id_company]);

  // Aviso de "ya cargué" para quien coordine el esqueleto de su pantalla
  // (ADR-2026-08-03-todas-las-secciones-cargan-igual). Va en un efecto sobre
  // `loading` y no adentro del `finally` de arriba para que también cubra el corte
  // temprano de "sin empresa", que baja `loading` sin pasar por ahí.
  useEffect(() => {
    if (!loading) onListo?.();
    // `onListo` no va en las dependencias a propósito: si el padre lo redefine en
    // cada render, esto se dispararía en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Derived flags for Rubro UI (component scope)
  const effectiveRubro = (rubroOption === 'Otro' ? customRubro.trim() : rubroOption);
  // `''` = todavía no hay rubro guardado (el centinela del alta por link, ya saneado
  // arriba). Antes acá caía a 'Moda e Indumentaria', que además de mostrar un rubro
  // falso hacía que elegir Moda no contara como cambio y el botón Guardar nunca apareciera.
  const currentRubro = (activeBrand?.rubro ?? company.rubro ?? '');
  // Solo mostrar Guardar si los inputs realmente corresponden a la marca activa
  // (evita el flash entre cambios de pill antes de que el useEffect sincronice).
  const rubroStateInSync = !activeBrand || rubroStateBrandIdRef.current === activeBrand.id;
  const showSaveRubro = rubroStateInSync
    // Con el select vacío no hay nada que guardar: sin esta condición el botón aparecía
    // solo apenas cargaba una marca sin rubro, y guardaba el vacío.
    && rubroOption !== ''
    && (rubroOption !== 'Otro' || customRubro.trim().length > 0)
    && ((effectiveRubro !== currentRubro) || savingRubro);

  /**
   * Avisa al resto de la app que la marca cambió — ADR-2026-07-31-editar-nombre-rubro-marca-refleja.
   *
   * Esta pantalla ya escuchaba `atlas:brand-changed` pero nunca lo emitía: sólo lo
   * disparaban los CAMBIOS de marca activa, no las EDICIONES de la marca. Editar el
   * nombre o el rubro refrescaba el formulario (`fetchBrands` local) y nadie más se
   * enteraba, así que la sidebar seguía mostrando el valor viejo hasta recargar.
   *
   * Se reusa el evento que ya existe en vez de inventar uno: los consumidores
   * (`useBrandProfile`, `Sidebar`, `InterfazV2`, …) reaccionan releyendo la marca,
   * que es exactamente lo que hace falta acá.
   */
  const avisarMarcaEditada = () => {
    window.dispatchEvent(
      new CustomEvent('atlas:brand-changed', {
        detail: { brandId: activeBrand?.id },
      }),
    );
  };

  const handleSaveRubro = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id_company) return;
    try {
      setSavingRubro(true);
      setError(null);
      setSuccess(null);

      if (activeBrand) {
        const { error: brandErr } = await supabase
          .from('brand_profiles')
          .update({ rubro: rubroInput })
          .eq('id', activeBrand.id);
        if (brandErr) throw brandErr;

        if (activeBrand.is_default) {
          const { error: compErr } = await supabase
            .from('company_profiles')
            .update({ rubro: rubroInput })
            .eq('id_company', profile.id_company);
          if (compErr) throw compErr;
          setCompany((prev) => ({ ...prev, rubro: rubroInput }));
          mergeLastCrearAnuncioState({ rubro: rubroInput });
        }

        await fetchBrands();
      } else {
        const { error } = await supabase
          .from('company_profiles')
          .update({ rubro: rubroInput })
          .eq('id_company', profile.id_company);
        if (error) throw error;
        setCompany((prev) => ({ ...prev, rubro: rubroInput }));
        mergeLastCrearAnuncioState({ rubro: rubroInput });
      }

      avisarMarcaEditada();
      setSuccess('Rubro actualizado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating rubro:', err);
      setError('No se pudo actualizar el rubro');
    } finally {
      setSavingRubro(false);
    }
  };

  const handleSaveMemoria = async () => {
    if (!activeBrand) return;
    try {
      setSavingMemoria(true);
      setError(null);
      setSuccess(null);
      const { error: brandErr } = await supabase
        .from('brand_profiles')
        .update({ context: memoriaInput })
        .eq('id', activeBrand.id);
      if (brandErr) throw brandErr;
      await fetchBrands();
      // La cabecera de Mi marca cuenta qué tiene completo la marca y lo relee con
      // este evento (ADR-2026-10-02-mi-marca-completitud).
      avisarMarcaEditada();
      setSuccess('Memoria actualizada correctamente');
      setTimeout(() => setSuccess(null), 3000);
      setIsEditingMemoria(false);
    } catch (err) {
      console.error('Error updating memoria:', err);
      setError('No se pudo actualizar la memoria');
    } finally {
      setSavingMemoria(false);
    }
  };

  const handleSaveColors = async () => {
    if (!activeBrand) return;
    const cleaned = colorsInput
      .map((c) => ({ ...c, hex: (c.hex || '').trim().toLowerCase() }))
      .filter((c) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(c.hex));
    try {
      setSavingColors(true);
      setError(null);
      setSuccess(null);
      const { error: brandErr } = await supabase
        .from('brand_profiles')
        .update({ colors: cleaned })
        .eq('id', activeBrand.id);
      if (brandErr) throw brandErr;
      await fetchBrands();
      // La paleta se ve también en la cabecera de Mi marca, que la relee con este
      // evento (ADR-2026-10-02-mi-marca-cabecera-de-identidad).
      avisarMarcaEditada();
      setSuccess('Colores actualizados correctamente');
      setTimeout(() => setSuccess(null), 3000);
      setIsEditingColors(false);
    } catch (err) {
      console.error('Error updating colors:', err);
      setError('No se pudieron actualizar los colores');
    } finally {
      setSavingColors(false);
    }
  };

  const handleSaveFonts = async () => {
    if (!activeBrand) return;
    const cleaned = fontsInput.map((f) => f.trim()).filter(Boolean);
    try {
      setSavingFonts(true);
      setError(null);
      setSuccess(null);
      const nextFonts = { ...((activeBrand.fonts as any) || {}), suggested: cleaned };
      const { error: brandErr } = await supabase
        .from('brand_profiles')
        .update({ fonts: nextFonts })
        .eq('id', activeBrand.id);
      if (brandErr) throw brandErr;
      await fetchBrands();
      // La cabecera de Mi marca cuenta qué tiene completo la marca y lo relee con
      // este evento (ADR-2026-10-02-mi-marca-completitud).
      avisarMarcaEditada();
      setSuccess('Tipografía actualizada correctamente');
      setTimeout(() => setSuccess(null), 3000);
      setIsEditingFonts(false);
    } catch (err) {
      console.error('Error updating fonts:', err);
      setError('No se pudo actualizar la tipografía');
    } finally {
      setSavingFonts(false);
    }
  };

  const handleSaveName = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!profile?.id_company) return;
    const newName = nameInput.trim();
    if (!newName) {
      setError(activeBrand ? 'El nombre de la marca no puede estar vacío' : 'El nombre de la empresa no puede estar vacío');
      return;
    }
    try {
      setSavingName(true);
      setError(null);
      setSuccess(null);

      if (activeBrand) {
        // Update brand_profiles row
        const { error: brandErr } = await supabase
          .from('brand_profiles')
          .update({ name: newName })
          .eq('id', activeBrand.id);
        if (brandErr) throw brandErr;

        // Si es la default, espejear a company_profiles para compatibilidad legacy
        if (activeBrand.is_default) {
          const { error: compErr } = await supabase
            .from('company_profiles')
            .update({ nombre: newName })
            .eq('id_company', profile.id_company);
          if (compErr) throw compErr;
          setCompany((prev) => ({ ...prev, nombre: newName }));
        }

        await fetchBrands();
        setSuccess('Nombre de la marca actualizado correctamente');
      } else {
        // Modo legacy: sin brand activa, escribir solo a company_profiles
        const { error } = await supabase
          .from('company_profiles')
          .update({ nombre: newName })
          .eq('id_company', profile.id_company);
        if (error) throw error;
        setCompany((prev) => ({ ...prev, nombre: newName }));
        setSuccess('Nombre de la empresa actualizado correctamente');
      }

      avisarMarcaEditada();
      setTimeout(() => setSuccess(null), 3000);
      setIsEditingName(false);
    } catch (err) {
      console.error('Error updating name:', err);
      setError('No se pudo actualizar el nombre');
    } finally {
      setSavingName(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profile?.id_company) return;
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];

    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo es demasiado grande. Máximo 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);
      setSuccess(null);

      const timestamp = new Date().getTime();
      const fileExt = file.name.split('.').pop();
      const fileName = `${timestamp}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('system')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('system')
        .getPublicUrl(filePath);

      if (activeBrand) {
        const { error: brandErr } = await supabase
          .from('brand_profiles')
          .update({ logo_url: publicUrl })
          .eq('id', activeBrand.id);
        if (brandErr) throw brandErr;

        // El logo nuevo se SUMA a "Logos" de Elementos de marca, sin sacar el anterior; de
        // fondo (ADR-2026-10-22-los-elementos-de-marca-arrancan-con-lo-de-la-marca).
        void sumarLogoAElementos({ companyId: profile.id_company, brandId: activeBrand.id }, file);

        // Si es la default, espejear a company_profiles (sidebar lo lee de ahí)
        if (activeBrand.is_default) {
          const { error: compErr } = await supabase
            .from('company_profiles')
            .update({ logo_url: publicUrl })
            .eq('id_company', profile.id_company);
          if (compErr) throw compErr;
          setCompany((prev) => ({ ...prev, logo_url: publicUrl }));
        }

        await fetchBrands();
      } else {
        // Legacy: sin brand activa
        const { error: updateError } = await supabase
          .from('company_profiles')
          .update({ logo_url: publicUrl })
          .eq('id_company', profile.id_company);
        if (updateError) throw updateError;
        setCompany((prev) => ({ ...prev, logo_url: publicUrl }));
      }

      // El logo se ve en varios lugares fuera de esta pantalla —el selector de marca,
      // la sidebar, el menú de marcas—, y cada uno lo tiene por su cuenta. Sin avisar,
      // seguían mostrando la inicial hasta recargar
      // (ADR-2026-08-05-el-logo-se-edita-desde-marca). Es el mismo evento con el que la
      // app se entera de un cambio de marca; el `brandId` viaja por compatibilidad con
      // quien lo lee, pero todos los que escuchan simplemente refrescan.
      window.dispatchEvent(
        new CustomEvent('atlas:brand-changed', {
          detail: { brandId: activeBrand?.id ?? null },
        }),
      );

      setSuccess('Logo actualizado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('No se pudo actualizar el logo');
    } finally {
      setUploadingLogo(false);
      // Clear the file input value
      (e.target as HTMLInputElement).value = '';
    }
  };

  // Nota: si se requiere eliminar logo, se puede reintroducir una acción que ponga logo_url en null.

  const handleDeleteBrand = async () => {
    if (!activeBrand) return;
    if (activeBrand.is_default) {
      setError('No se puede eliminar la marca principal');
      return;
    }
    if (brands.length <= 1) {
      setError('Debe quedar al menos una marca');
      return;
    }
    try {
      setDeletingBrand(true);
      setError(null);
      setSuccess(null);

      const brandIdToDelete = activeBrand.id;

      const { error: delError } = await supabase
        .from('brand_profiles')
        .delete()
        .eq('id', brandIdToDelete);

      if (delError) throw delError;

      // Pasar a la marca default antes de refrescar
      const defaultBrand = brands.find((b) => b.is_default && b.id !== brandIdToDelete);
      if (defaultBrand) {
        setActiveBrandId(defaultBrand.id);
        // Actualizar current_brand_id del usuario si apuntaba a la borrada
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('profiles')
            .update({ current_brand_id: defaultBrand.id })
            .eq('id', user.id);
        }
      }

      setShowDeleteBrandModal(false);
      await fetchBrands();
      setSuccess('Marca eliminada correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting brand:', err);
      setError('No se pudo eliminar la marca');
    } finally {
      setDeletingBrand(false);
    }
  };

  const handleDeleteAnalysis = async () => {
    if (!profile?.id_company) return;
    try {
      setDeletingAnalysis(true);
      setError(null);
      setSuccess(null);

      if (activeBrand) {
        const { error: brandErr } = await supabase
          .from('brand_profiles')
          .update({ context: null })
          .eq('id', activeBrand.id);
        if (brandErr) throw brandErr;

        // Si es la default, también limpiar el legacy config.media_agent_analysis
        if (activeBrand.is_default) {
          const { error: cfgErr } = await supabase
            .from('config')
            .update({ media_agent_analysis: null })
            .eq('id_company', profile.id_company);
          if (cfgErr) throw cfgErr;
          setMarketingStrategy(null);
        }

        await fetchBrands();
      } else {
        const { error } = await supabase
          .from('config')
          .update({ media_agent_analysis: null })
          .eq('id_company', profile.id_company);
        if (error) throw error;
        setMarketingStrategy(null);
      }

      setShowDeleteModal(false);
      setSuccess('Análisis de marca eliminado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting brand analysis:', err);
      setError('No se pudo eliminar el análisis de marca');
    } finally {
      setDeletingAnalysis(false);
    }
  };

  // Refs de las tarjetas, para llevar a la vista la que abre un `pedido`. Estos hooks
  // van ANTES de los `return` tempranos (cargando / sin empresa).
  const tarjetaColoresRef = useRef<HTMLDivElement>(null);
  const tarjetaFuentesRef = useRef<HTMLDivElement>(null);
  const tarjetaMemoriaRef = useRef<HTMLDivElement>(null);

  /**
   * Atender el `pedido` (ver la prop) cuando los datos ya están: abre la MISMA edición
   * que el lápiz de cada tarjeta (o el selector del logo) y la trae a la vista.
   */
  const pedidoAtendidoRef = useRef<number | null>(null);
  useEffect(() => {
    if (!pedido || loading || !activeBrand) return;
    if (pedidoAtendidoRef.current === pedido.n) return;
    pedidoAtendidoRef.current = pedido.n;
    let tarjeta: HTMLDivElement | null = null;
    if (pedido.que === 'logo') {
      fileInputRef.current?.click();
    } else if (pedido.que === 'colores') {
      const colores = Array.isArray(activeBrand.colors) ? (activeBrand.colors as any[]) : [];
      setColorsInput(colores.map((c: any) => ({ hex: c.hex, name: c.name, role: c.role })));
      setIsEditingColors(true);
      tarjeta = tarjetaColoresRef.current;
    } else if (pedido.que === 'tipografia') {
      // Sin tipografía sugerida se arranca de Inter / Inter (la del sistema), que
      // la persona cambia en los selects.
      const sugeridas = (activeBrand.fonts as any)?.suggested;
      setFontsInput(
        Array.isArray(sugeridas) && sugeridas.length ? sugeridas.slice(0, 2) : ['Inter', 'Inter'],
      );
      setIsEditingFonts(true);
      tarjeta = tarjetaFuentesRef.current;
    } else if (pedido.que === 'memoria') {
      setMemoriaInput(activeBrand.context || '');
      setIsEditingMemoria(true);
      tarjeta = tarjetaMemoriaRef.current;
    }
    // Después del render que monta la edición.
    if (tarjeta) {
      const t = tarjeta;
      requestAnimationFrame(() => t.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
    }
    onPedidoAtendido?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido?.n, loading, activeBrand?.id]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? "py-12" : "bg-surface-card rounded-lg border border-neutral-200 p-6"}`}>
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!profile?.id_company) {
    return (
      <div className={embedded ? "" : "bg-surface-card rounded-lg border border-neutral-200 p-5"}>
        <p className="text-[15px] text-neutral-500">Primero vinculá tu empresa en la sección Empleados.</p>
      </div>
    );
  }

  // Valores que efectivamente se muestran: si hay marca activa, usar sus datos.
  // Si no (free tier sin onboarding), fallback a company_profiles (vista legacy).
  // IMPORTANTE: cuando hay activeBrand y su logo_url es null (user salteó), NO caemos
  // a company.logo_url porque eso muestra el logo de OTRA marca (la default vieja).
  // En su lugar, dejamos null para que se muestre el placeholder de Atlas.
  const displayLogoUrl = activeBrand
    ? (activeBrand.logo_url || null)
    : (company.logo_url || null);

  /**
   * Las cuatro tarjetas de esta pantalla (marca, colores, tipografía, memoria).
   *
   * En "Marca" van BLANCAS: ahí quedan sueltas sobre el lienzo gris del shell, y el
   * `surface-elevated` —que es un gris apenas más claro— las dejaba casi sin
   * separarse del fondo. En `/configuracion` siguen elevadas: viven DENTRO de un
   * contenedor blanco, donde el blanco sobre blanco borraría el borde de cada una.
   * O sea el mismo criterio en los dos lados —que la tarjeta se despegue de lo que
   * tiene detrás—, con el color que corresponde a cada fondo.
   */
  const TARJETA = `${
    embedded ? 'bg-surface-card' : 'bg-surface-elevated'
  } border border-neutral-200 rounded-lg p-4`;
  /**
   * La tarjeta que se está editando se marca: borde en el azul de marca, un halo
   * suave y "Editando" junto al rótulo — ADR-2026-10-02-mi-marca-tarjeta-en-edicion.
   * Con cuatro tarjetas y un lápiz en cada una no quedaba claro cuál estaba abierta
   * (y cuál tenía cambios sin guardar). `scroll-mt-24`: para que el `scrollIntoView`
   * del pedido no la deje pegada al borde.
   */
  const claseTarjeta = (editando: boolean) =>
    `${TARJETA} scroll-mt-24 transition-[border-color,box-shadow] duration-200 ${
      editando ? '!border-[#1644EB] shadow-[0_0_0_4px_#EBEFFE]' : ''
    }`;
  const etiquetaEditando = (
    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#EBEFFE] px-2 py-0.5 align-middle text-[10.5px] font-semibold normal-case tracking-normal text-brand-primary">
      <span className="h-1.5 w-1.5 rounded-full bg-brand-primary motion-safe:animate-pulse" />
      Editando
    </span>
  );
  // El rubro no tiene lápiz: se edita al cambiar el select. Está "en edición" desde
  // que el valor elegido deja de ser el guardado (incluido "Otro" con el texto vacío,
  // que todavía no muestra Guardar pero ya es una edición a medias).
  const rubroEnEdicion =
    rubroStateInSync &&
    (rubroOption === 'Otro'
      ? customRubro.trim() !== currentRubro
      : rubroOption !== '' && rubroOption !== currentRubro);
  const marcaEnEdicion = isEditingName || rubroEnEdicion || uploadingLogo;

  const displayName = (activeBrand?.name ?? company.nombre) || '';
  // `displayRubro` se fue: el rubro que se muestra ahora sale de `rubroVisible`, que
  // devuelve `null` cuando todavía no hay uno (el centinela del alta por link) para que
  // el select caiga en su placeholder en vez de pintar "sin-definir".
  // Ver ADR-2026-09-22-el-centinela-no-se-le-muestra-al-cliente.
  const displayAnalysis = activeBrand?.context || marketingStrategy;
  const displayColors = Array.isArray(activeBrand?.colors) ? (activeBrand!.colors as any[]) : [];
  const displayFonts = (activeBrand?.fonts as any) || null;
  const fuentesSugeridas: string[] = Array.isArray(displayFonts?.suggested)
    ? (displayFonts.suggested as string[])
    : [];
  // Opciones del dropdown de fuentes al editar: las 17 curadas + las fuentes
  // propias de la marca (actuales/originales), para que la fuente elegida
  // aparezca pre-seleccionada y la original siempre se pueda volver a elegir
  // aunque no esté en la lista curada.
  const fontFamilyOptions = (() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    TEXT_FONT_OPTIONS.forEach((o) => {
      const k = o.family.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      opts.push({ value: o.family, label: o.label || o.family });
    });
    const fromBrand: string[] = Array.isArray(displayFonts?.suggested)
      ? (displayFonts.suggested as string[])
      : [];
    [...fromBrand, ...fontsInput].forEach((fam) => {
      const f = (fam || '').trim();
      if (!f || seen.has(f.toLowerCase())) return;
      seen.add(f.toLowerCase());
      opts.push({ value: f, label: f });
    });
    return opts;
  })();
  const canDeleteBrand = !!activeBrand && !activeBrand.is_default && brands.length > 1;

  return (
    <div className={embedded ? "" : "bg-surface-card rounded-lg border border-neutral-200"}>
      {/* Header "Mis marcas" + "Añadir nueva marca" — oculto en modo embebido
          (en la tab Mi diseño de Mi marca ya estás en ese contexto). */}
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 border-b border-neutral-200">
          <div>
            <h2 className="text-[17px] font-semibold text-brand-black/90">Mis marcas</h2>
            <p className="text-[13px] text-neutral-500 mt-0.5">
              Identidad visual y estrategia de cada una de tus marcas
            </p>
          </div>
          <button
            type="button"
            onClick={async () => {
              const plan = await getUserPlan();
              if (!planAllowsMultiBrand(plan)) {
                setShowMultiBrandRequiredModal(true);
                return;
              }
              navigate('/redes-sociales/descripcion-brand?mode=additional');
            }}
            className="inline-flex items-center justify-center gap-2 h-9 px-4 border border-neutral-200 hover:border-neutral-300 bg-white rounded-md text-[13px] font-medium text-brand-black/90 transition-colors shrink-0"
          >
            <Plus className="w-4 h-4" />
            Añadir nueva marca
          </button>
        </div>
      )}

      {/* Pills de marcas — solo visible si el user tiene >1 marca.
          En modo embebido (tab "Mi diseño") se ocultan.

          ⚠️ El motivo original ya NO existe: se ocultaban porque el cambio de
          marca se hacía desde la card que `MiMarca` tenía arriba, y esa card se
          quitó (ADR-2026-07-31-mi-marca-card-marca-pantalla). Hoy, con >1 marca, el tab "Mi diseño" no ofrece ninguna
          forma de elegir cuál se está editando — hay que salir al `Sidebar` de la
          app. Se dejó como estaba a propósito (el pedido fue sacar la card, no
          rediseñar esto), pero si aparece el problema, mostrar las pills también
          embebidas es sacar el `!embedded` de la condición de abajo. */}
      {!embedded && brands.length > 1 && (
        <div className="border-b border-neutral-200">
          <div
            ref={pillsScrollRef}
            onMouseDown={onPillsMouseDown}
            onMouseMove={onPillsMouseMove}
            onMouseUp={stopPillsDrag}
            onMouseLeave={stopPillsDrag}
            className={`pt-4 pb-3 overflow-x-auto cursor-grab active:cursor-grabbing select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${embedded ? "" : "px-5"}`}
          >
            <div className="flex items-center gap-2 min-w-min">
              {brands.map((b) => {
                const isActive = b.id === activeBrandId;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={(e) => {
                      // Si venimos de un drag, no activar la pill
                      if (dragStateRef.current.moved) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      setActiveBrandId(b.id);
                    }}
                    className={`inline-flex items-center gap-2 h-9 pl-1.5 pr-3 rounded-md text-[13px] font-medium transition-colors shrink-0 border ${
                      isActive
                        ? 'bg-brand-primary/10 border-brand-primary/30 text-brand-primary'
                        : 'bg-white border-neutral-200 hover:border-neutral-300 text-brand-black/80'
                    }`}
                  >
                    <img
                      src={
                        b.logo_url
                          ? (b.logo_url.includes('supabase.co')
                              ? `${b.logo_url}?width=32&height=32`
                              : b.logo_url)
                          : '/images/estudio-atlas-logo-solo.svg'
                      }
                      alt=""
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      className="w-6 h-6 rounded-[5px] object-contain bg-white border border-neutral-200 shrink-0 pointer-events-none"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/images/estudio-atlas-logo-solo.svg';
                      }}
                    />
                    <span className="truncate max-w-[140px] pointer-events-none">{b.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div
        className={
          embedded
            ? "space-y-3 sm:space-y-4 lg:space-y-0 lg:flex lg:flex-wrap lg:gap-3 lg:items-stretch"
            : "p-4 sm:p-5 space-y-3 sm:space-y-4"
        }
      >
        {/* Card: Logo + Nombre + Rubro.
            En embebido, marca · colores · tipografía van en UNA fila (40/30/30: la de
            marca lleva logo + select de rubro y en un tercio el rubro se cortaba) y la
            memoria debajo a todo el ancho — ADR-2026-10-02-mi-marca-informacion-en-tercios.
            `grow` en las tres: si falta una (sin marca activa no hay colores; sin
            tipografía sugerida no hay esa tarjeta), las otras ocupan su lugar.
            `min-w-0` porque un ítem flex no se achica por debajo de su contenido: la
            tarjeta de marca pedía ~490px y empujaba a la tipografía a otra fila. */}
        <div className={`${claseTarjeta(marcaEnEdicion)} ${embedded ? "lg:order-1 lg:basis-[calc(40%-8px)] lg:grow lg:min-w-0" : ""}`}>
          <div className="flex items-start gap-3 sm:gap-4">
            {/* Logo a la izquierda — ADR-2026-08-05-el-logo-se-edita-desde-marca.
                Estaba oculto en modo embebido "porque ya se ve arriba en la card de
                marca", pero esa card se fue en ADR-2026-07-31-mi-marca-card-marca-pantalla
                y con ella el único acceso: desde "Marca" no había forma de cargar ni
                cambiar el logo. Se muestra en los dos modos.

                Sin logo NO se dibuja el de Atlas de relleno: era el logo de otra
                empresa en el lugar de la propia, y encima tapaba que faltaba cargarlo.
                Va un marco vacío con el ícono de "sumar imagen", y el botón dice
                "Agregar" en vez de "Editar". */}
            <div className="shrink-0 flex flex-col items-center gap-2">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border border-neutral-200 bg-white">
                {displayLogoUrl && logoRoto !== displayLogoUrl ? (
                  <img
                    src={displayLogoUrl}
                    alt="Logo de la marca"
                    className="w-full h-full object-contain p-1"
                    // Si la URL no carga (borrada del bucket, firma vencida) se cae al
                    // vacío, que es lo que de verdad pasa, y el botón vuelve a ofrecer
                    // cargar uno.
                    onError={() => setLogoRoto(displayLogoUrl)}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    aria-label="Agregar logo"
                    className="grid h-full w-full place-items-center text-neutral-300 hover:bg-neutral-50 hover:text-neutral-400 transition-colors"
                  >
                    <ImagePlus className="w-7 h-7" strokeWidth={1.5} />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="h-7 px-2.5 rounded-md bg-surface-card border border-neutral-200 text-[11px] font-medium text-brand-black/90 hover:bg-neutral-50 shadow-sm transition-colors"
              >
                {uploadingLogo ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : displayLogoUrl && logoRoto !== displayLogoUrl ? (
                  'Editar'
                ) : (
                  'Agregar'
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                id="company-logo-upload"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                disabled={uploadingLogo}
              />
            </div>

            {/* Nombre + Rubro, uno debajo del otro. En embebido iban lado a lado en
                lg, pero en una tarjeta de un tercio el select del rubro quedaba
                cortado ("Moda e Indum…") — ADR-2026-10-02-mi-marca-informacion-en-tercios. */}
            <div className="flex-1 min-w-0">
              <div>
              <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-1.5">
                {activeBrand ? 'Nombre de la marca' : 'Nombre de la empresa'}
                {isEditingName && etiquetaEditando}
              </div>
              <div className="flex items-center gap-2 mb-3">
                {!isEditingName ? (
                  <div className="flex-1 min-w-0 text-[16px] sm:text-[18px] font-semibold text-brand-black/90 truncate">
                    {displayName || 'Sin nombre'}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSaveName();
                      }
                    }}
                    placeholder="Nombre de la marca"
                    autoFocus
                    className="flex-1 min-w-0 h-9 px-3 border border-neutral-200 rounded-md text-[14px] text-brand-black/90 bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                  />
                )}
                <EditConfirmButton
                  isEditing={isEditingName}
                  hasChanges={
                    nameInput.trim().length > 0 &&
                    nameInput.trim() !== (displayName || '').trim()
                  }
                  saving={savingName}
                  onEdit={() => {
                    setNameInput(displayName || '');
                    setIsEditingName(true);
                  }}
                  onSave={() => handleSaveName()}
                  onCancel={() => {
                    setIsEditingName(false);
                    setNameInput(displayName || '');
                  }}
                  label="nombre"
                />
              </div>
              </div>
              <div>
              <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-1.5">
                Rubro
                {rubroEnEdicion && etiquetaEditando}
              </div>
              <form onSubmit={handleSaveRubro} className="flex flex-wrap items-center gap-2">
                <CustomSelect
                  options={rubros.map((r) => ({ value: r, label: r }))}
                  value={rubroOption}
                  // Lo que se lee cuando la marca todavía no tiene rubro: un pedido, no
                  // un valor. El default de `CustomSelect` ("Seleccionar...") no dice qué.
                  placeholder="Elegí tu rubro"
                  onChange={(v) => {
                    setRubroOption(v);
                    if (v !== 'Otro') {
                      setCustomRubro('');
                      setRubroInput(v);
                    } else {
                      setRubroInput(customRubro);
                    }
                  }}
                  className="flex-1 min-w-0 max-w-full"
                />
                {rubroOption === 'Otro' && (
                  <input
                    type="text"
                    value={customRubro}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomRubro(val);
                      setRubroInput(val);
                    }}
                    placeholder="Especificá tu rubro"
                    className="w-full h-9 px-3 rounded-md bg-surface-card border border-neutral-200 text-[14px] text-brand-black/90 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                    required
                  />
                )}
                {showSaveRubro && (
                  <button
                    type="submit"
                    disabled={savingRubro}
                    className="h-9 px-3 bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
                  >
                    {savingRubro ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar'}
                  </button>
                )}
              </form>
              </div>
            </div>
          </div>
        </div>

        {/* Card: Tipografía — editable (Principal / Secundaria) */}
        {/* También se muestra en edición sin fuentes sugeridas: así la abre el
            "Agregar" de "Completá tu marca" (ADR-2026-10-02-mi-marca-agregar-directo). */}
        {(fuentesSugeridas.length > 0 || isEditingFonts) && (
          <div
            ref={tarjetaFuentesRef}
            className={`${claseTarjeta(isEditingFonts)} ${embedded ? "lg:order-3 lg:basis-[calc(30%-8px)] lg:grow lg:min-w-0" : ""}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                Tipografía
                {isEditingFonts && etiquetaEditando}
              </div>
              {activeBrand && (
                <EditConfirmButton
                  isEditing={isEditingFonts}
                  hasChanges={
                    JSON.stringify(fontsInput) !==
                    JSON.stringify(fuentesSugeridas.slice(0, 2))
                  }
                  saving={savingFonts}
                  onEdit={() => {
                    setFontsInput(fuentesSugeridas.slice(0, 2));
                    setIsEditingFonts(true);
                  }}
                  onSave={handleSaveFonts}
                  onCancel={() => setIsEditingFonts(false)}
                  label="tipografía"
                />
              )}
            </div>
            {isEditingFonts ? (
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  {fontsInput.map((font, idx) => (
                    <div key={idx} className="flex flex-col items-center text-center gap-2">
                      <div
                        className="text-[40px] sm:text-[48px] leading-none text-brand-black/90"
                        style={{ fontFamily: `"${font}", sans-serif` }}
                      >
                        Aa
                      </div>
                      <div className="text-[10px] text-neutral-500 uppercase tracking-wide">
                        {idx === 0 ? 'Principal' : 'Secundaria'}
                      </div>
                      <CustomSelect
                        options={fontFamilyOptions}
                        value={font}
                        onChange={(val) =>
                          setFontsInput((prev) => {
                            const next = [...prev];
                            next[idx] = val;
                            return next;
                          })
                        }
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Un espécimen: el nombre de la marca escrito en SU fuente principal y
                    un abecedario corto, en vez de un "Aa" suelto —
                    ADR-2026-10-02-mi-marca-informacion-visual. Debajo, cada fuente
                    con su rol, escrita en sí misma. */}
                <div
                  // Un nombre largo ("Ciudad de Muebles") no entraba a 34px en la tarjeta.
                  className={`truncate leading-[1.1] tracking-[-0.01em] text-[#16181D] ${
                    (displayName || '').length > 12 ? 'text-[26px]' : 'text-[34px]'
                  }`}
                  style={{ fontFamily: `"${fuentesSugeridas[0]}", serif` }}
                >
                  {displayName || 'Aa'}
                </div>
                <div
                  className="mt-1 truncate text-[13px] text-black/45"
                  style={{ fontFamily: `"${fuentesSugeridas[0]}", serif` }}
                >
                  Aa Bb Cc Dd Ee 0123
                </div>
                <div className="mt-4 divide-y divide-black/[0.06] border-t border-black/[0.06]">
                  {fuentesSugeridas.slice(0, 2).map((font: string, idx: number) => (
                    <div
                      key={`${font}-${idx}`}
                      className="flex items-baseline justify-between gap-3 py-2"
                    >
                      <span className="shrink-0 text-[10px] uppercase tracking-wider text-neutral-500">
                        {idx === 0 ? 'Principal' : 'Secundaria'}
                      </span>
                      <span
                        className="truncate text-[15px] text-[#16181D]"
                        style={{ fontFamily: `"${font}", serif` }}
                      >
                        {font}
                      </span>
                    </div>
                  ))}
                </div>
                {displayFonts?.style && (
                  <p className="text-[11px] leading-snug text-neutral-500 italic pt-3 border-t border-black/[0.06]">
                    {displayFonts.style}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Card: Colores — editable (agregar / editar / quitar) */}
        {activeBrand && (
          <div
            ref={tarjetaColoresRef}
            className={`${claseTarjeta(isEditingColors)} ${embedded ? "lg:order-2 lg:basis-[calc(30%-8px)] lg:grow lg:min-w-0" : ""}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                Paleta de colores
                {isEditingColors && etiquetaEditando}
              </div>
              <div className="flex items-center gap-2">
                <EditConfirmButton
                  isEditing={isEditingColors}
                  hasChanges={
                    JSON.stringify(colorsInput) !==
                    JSON.stringify(
                      displayColors.map((c: any) => ({
                        hex: c.hex,
                        name: c.name,
                        role: c.role,
                      })),
                    )
                  }
                  saving={savingColors}
                  onEdit={() => {
                    setColorsInput(
                      displayColors.map((c: any) => ({
                        hex: c.hex,
                        name: c.name,
                        role: c.role,
                      })),
                    );
                    setIsEditingColors(true);
                  }}
                  onSave={handleSaveColors}
                  onCancel={() => setIsEditingColors(false)}
                  label="colores"
                />
              </div>
            </div>
            {/* El aviso va arriba de los colores y no en el encabezado: ahí, con el
                rótulo, "Editando" y la cruz, no entraba en una tarjeta de 30%. */}
            {isEditingColors && (
              <p className="mb-2 inline-flex items-center gap-1 text-[11px] text-neutral-400">
                <Pencil className="w-3 h-3" />
                Tocá un color para cambiarlo
              </p>
            )}
            {isEditingColors ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-3 flex-wrap">
                  {colorsInput.map((c, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-1.5">
                      <div className="relative group">
                        <label
                          className="relative w-12 h-12 rounded-full border border-neutral-200 shadow-sm cursor-pointer block transition-transform hover:scale-105"
                          style={{ backgroundColor: c.hex }}
                          title={c.hex}
                        >
                          <span className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <Pencil className="w-4 h-4 text-white" />
                          </span>
                          <input
                            type="color"
                            value={c.hex}
                            onChange={(e) =>
                              setColorsInput((prev) =>
                                prev.map((x, i) =>
                                  i === idx ? { ...x, hex: e.target.value } : x,
                                ),
                              )
                            }
                            className="opacity-0 absolute inset-0 cursor-pointer rounded-full"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setColorsInput((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                          className="absolute -top-1 -right-1 w-4 h-4 inline-flex items-center justify-center rounded-full bg-white text-neutral-500 ring-1 ring-black/10 shadow-sm opacity-0 group-hover:opacity-100 hover:text-neutral-800 transition-all"
                          aria-label="Quitar color"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                      <div className="text-[9px] sm:text-[10px] text-neutral-500 font-mono uppercase">
                        {c.hex}
                      </div>
                    </div>
                  ))}
                  {/* Añadir color: agrega UN color neutro y después se edita
                      tocándolo (un color input acá dispararía onChange por cada
                      paso del arrastre y agregaría cientos de colores). */}
                  <button
                    type="button"
                    onClick={() =>
                      setColorsInput((prev) => [...prev, { hex: '#888888' }])
                    }
                    className="w-12 h-12 rounded-full border border-dashed border-neutral-300 text-neutral-400 hover:border-brand-primary/40 hover:text-brand-primary cursor-pointer flex items-center justify-center transition-colors"
                    title="Añadir color"
                    aria-label="Añadir color"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : displayColors.length > 0 ? (
              // Muestras grandes, como una carta de colores, y tocar una copia su hex —
              // ADR-2026-10-02-mi-marca-informacion-visual. Antes eran círculos de 48px
              // con el hex en 10px abajo: la paleta, que es lo más visual de la marca,
              // era lo que menos se veía.
              <div className="grid grid-cols-[repeat(auto-fit,minmax(52px,1fr))] gap-2">
                {displayColors.slice(0, 6).map((c: any, idx: number) => {
                  const claro = esColorClaro(c.hex);
                  return (
                    <button
                      key={`${c.hex}-${idx}`}
                      type="button"
                      onClick={() => copiarHex(c.hex, idx)}
                      title={`Copiar ${String(c.hex).toUpperCase()}`}
                      className="group/color relative flex h-[104px] flex-col justify-end rounded-2xl p-2 text-left shadow-[inset_0_0_0_1px_rgba(16,24,40,0.08)] transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.97]"
                      style={{ backgroundColor: c.hex }}
                    >
                      <span
                        className={`flex items-center gap-1 font-mono text-[10.5px] font-medium tracking-tight ${claro ? 'text-black/60' : 'text-white/85'}`}
                      >
                        {hexCopiado === idx ? (
                          <>
                            <Check className="h-3 w-3 shrink-0" />
                            Copiado
                          </>
                        ) : (
                          <>
                            {String(c.hex).replace('#', '').toUpperCase()}
                            <Copy className="h-2.5 w-2.5 shrink-0 opacity-0 transition-opacity group-hover/color:opacity-100" />
                          </>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-[13px] text-neutral-500">
                Esta marca todavía no tiene colores. Tocá el lápiz para
                agregarlos.
              </p>
            )}
          </div>
        )}

        {/* Card: Memoria de la IA (análisis) — editable */}
        <div
          ref={tarjetaMemoriaRef}
          className={`${claseTarjeta(isEditingMemoria)} ${embedded ? "lg:order-4 lg:basis-full" : ""}`}
        >
          <div className="flex items-center justify-between mb-3">
            {/* Con ícono y bajada: es lo que la IA lee de la marca cada vez que crea, y
                como un rótulo gris más se leía como un campo cualquiera —
                ADR-2026-10-02-mi-marca-informacion-visual. */}
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#EBEFFE] to-[#F4EEFF] text-brand-primary">
                <Sparkles className="h-4 w-4" />
              </span>
              <div>
                <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider leading-tight">
                  Memoria de la IA
                  {isEditingMemoria && etiquetaEditando}
                </div>
                <div className="text-[12px] text-black/40 leading-tight mt-0.5">
                  Lo que Atlas tiene en cuenta cada vez que crea para esta marca
                </div>
              </div>
            </div>
            {activeBrand && (
              <EditConfirmButton
                isEditing={isEditingMemoria}
                hasChanges={memoriaInput !== (activeBrand.context || '')}
                saving={savingMemoria}
                onEdit={() => {
                  setMemoriaInput(activeBrand.context || '');
                  setIsEditingMemoria(true);
                }}
                onSave={handleSaveMemoria}
                onCancel={() => setIsEditingMemoria(false)}
                label="memoria"
              />
            )}
          </div>
          {isEditingMemoria ? (
            <div className="flex flex-col gap-2">
              <textarea
                value={memoriaInput}
                onChange={(e) => setMemoriaInput(e.target.value)}
                rows={10}
                placeholder="Anotá tono, público, producto estrella, lo que evitar y cualquier detalle que la IA deba recordar al escribir o diseñar."
                className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[13px] leading-relaxed text-brand-black/90 bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors resize-y min-h-[180px]"
              />
            </div>
          ) : displayAnalysis ? (
            <div className="relative text-[13px] text-brand-black/80">
              <div
                className={`${isStrategyExpanded ? 'pb-12' : 'line-clamp-[8] pb-10'}${embedded ? ' lg:line-clamp-none lg:pb-0' : ''}`}
              >
                {renderMarkdown(displayAnalysis)}
              </div>
              {!isStrategyExpanded && (
                <div
                  className={`pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-surface-elevated via-surface-elevated/80 to-transparent${embedded ? ' lg:hidden' : ''}`}
                />
              )}
              {/* En desktop embebido mostramos todo (sin Ver más/menos). */}
              <div className={`absolute bottom-0 right-0 z-10${embedded ? ' lg:hidden' : ''}`}>
                <button
                  type="button"
                  onClick={() => setIsStrategyExpanded(v => !v)}
                  className="h-8 px-3.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors"
                >
                  {isStrategyExpanded ? 'Ver menos' : 'Ver más'}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-[13px] text-neutral-500">
              No hay información disponible todavía. Completá la descripción de marca para generar el análisis.
            </div>
          )}
        </div>

        {/* Delete brand (only for non-default brands when there are multiple).
            Oculto en modo embebido ("Mi diseño"): eliminar una marca vive ahora
            en Configuración → Mis marcas. */}
        {!embedded && canDeleteBrand && (
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setShowDeleteBrandModal(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-10 px-4 border border-red-200 text-red-600 hover:bg-red-50 rounded-md text-[13px] font-medium transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Eliminar esta marca
            </button>
          </div>
        )}
      </div>

      {/* Delete Analysis Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-surface-card rounded-lg shadow-lg max-w-sm w-full p-5 relative" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowDeleteModal(false)}
              className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-5">
              <div className="mx-auto w-10 h-10 rounded-md bg-red-50 border border-red-200 flex items-center justify-center mb-3">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1">Eliminar análisis de marca</h3>
              <p className="text-[13px] text-neutral-500">
                Se eliminará el análisis de la marca realizado por la IA. Podés volver a generarlo desde la pestaña de <strong>Estudio Virtual</strong>.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteAnalysis}
                disabled={deletingAnalysis}
                className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white text-[15px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
              >
                {deletingAnalysis ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Eliminar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Brand Confirmation Modal */}
      {showDeleteBrandModal && activeBrand && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
          onClick={() => !deletingBrand && setShowDeleteBrandModal(false)}
        >
          <div className="bg-surface-card rounded-lg shadow-lg max-w-sm w-full p-5 relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowDeleteBrandModal(false)}
              disabled={deletingBrand}
              className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-5">
              <div className="mx-auto w-10 h-10 rounded-md bg-red-50 border border-red-200 flex items-center justify-center mb-3">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1">Eliminar marca</h3>
              <p className="text-[13px] text-neutral-500">
                Vas a eliminar la marca <strong>{activeBrand.name}</strong>. Se perderá su identidad visual, análisis y los productos vinculados quedarán sin marca asignada. Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5">
              <button
                onClick={() => setShowDeleteBrandModal(false)}
                disabled={deletingBrand}
                className="h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteBrand}
                disabled={deletingBrand}
                className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white text-[15px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
              >
                {deletingBrand ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Eliminar marca'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <SubscriptionLimitModal
        isOpen={showMultiBrandRequiredModal}
        onClose={() => setShowMultiBrandRequiredModal(false)}
        type="multi-brand-required"
      />
    </div>
  );
};
